import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { formatDate } from "@progress/kendo-intl";
import { toast } from "react-toastify";
import { Tile } from "components/UIComponents/Tile";
import DataList from "components/MatterDetails/DataList";
import SubmitButton from "components/common/SubmitButton";
import { TransactionPeriod } from "components/common/TransactionPeriod";
import { AmountFormat, formatDateForAPI } from "components/common/formatter";
import { useNominalsBySubType } from "services/nominalsService";
import { NominalSubtypes } from "components/Enums/Nominals.ts";
import { getSelectedSessionPeriodNumber } from "services/transactionPeriodService";
import { createposting } from "api/accountsAPI";
import { getMatterDefaults } from "api";

const WRITE_OFF_POSTING_TYPE = "BWO";
const WRITE_OFF_REFERENCE = "Bill W/O";

const BILL_POSTING_TYPE = "BILL";

const MIN_BILL_AGE_MONTHS = 6;
const MAX_BILL_AGE_MONTHS = 54;

export const isBillPosting = (postingType) =>
  String(postingType ?? "").toUpperCase() === BILL_POSTING_TYPE;

export const getBillWriteOffEligibility = (billDate) => {
  const date = billDate ? new Date(billDate) : null;
  if (!date || Number.isNaN(date.getTime())) {
    return { allowed: false, message: "Bill date is not available" };
  }

  const today = new Date();
  let months =
    (today.getFullYear() - date.getFullYear()) * 12 +
    (today.getMonth() - date.getMonth());
  if (today.getDate() < date.getDate()) months -= 1;

  if (months < MIN_BILL_AGE_MONTHS) {
    return { allowed: false, message: "Bill must be 6 months old" };
  }
  if (months > MAX_BILL_AGE_MONTHS) {
    return { allowed: false, message: "Bill is older than 4 years and 6 months" };
  }
  return { allowed: true, message: "" };
};

const round2 = (value) => {
  const amount = Number(value) || 0;
  const sign = amount < 0 ? -1 : 1;
  const scaled = Number((Math.abs(amount) * 100).toPrecision(15));
  return (sign * Math.round(scaled)) / 100;
};

const resolveBillWriteOffNominalRef = async (matterID) => {
  if (!matterID) return null;
  try {
    const response = await getMatterDefaults(matterID);
    return response?.data?.data?.defaultBillWriteOff ?? null;
  } catch (error) {
    console.error("Failed to resolve bill write off nominal", error);
    return null;
  }
};

const BillWriteOffDialog = ({
  transNo,
  postingId,
  postingInfo = {},
  matterRef,
  amountPaid,
  allocations = [],
  onClose,
  onWriteOff,
}) => {
  const [writeOffDate, setWriteOffDate] = useState(new Date());
  const [nominal, setNominal] = useState(null);
  const [nominalFilter, setNominalFilter] = useState("");
  const [period, setPeriod] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: expenseNominals = [] } = useNominalsBySubType(
    NominalSubtypes.PL_EXPENSE,
    { excludeArchived: true },
  );

  const netAmount = Number(postingInfo?.netAmount) || 0;
  const vatAmount = Number(postingInfo?.vatAmount) || 0;
  const grossAmount = round2(netAmount + vatAmount);

  const outstanding = useMemo(() => {
    const allocatedTotal = (allocations || []).reduce(
      (total, item) => total + Math.abs(Number(item?.amount) || 0),
      0,
    );
    const paid = round2(amountPaid ?? allocatedTotal);
    const unpaidGross = round2(Math.max(grossAmount - paid, 0));
    const netWriteOff = grossAmount
      ? round2((unpaidGross * netAmount) / grossAmount)
      : unpaidGross;

    return {
      amountPaid: paid,
      amountDue: unpaidGross,
      netWriteOff,
      vatToClaim: round2(unpaidGross - netWriteOff),
    };
  }, [allocations, amountPaid, grossAmount, netAmount]);

  useEffect(() => {
    let cancelled = false;

    const applyDefaultNominal = async () => {
      if (!expenseNominals.length) return;
      const defaultRef = await resolveBillWriteOffNominalRef(postingInfo?.matterID);
      if (cancelled || !defaultRef) return;

      const defaultNominal = expenseNominals.find(
        (item) => String(item.nominalRef) === String(defaultRef),
      );
      if (defaultNominal) setNominal(defaultNominal);
    };

    applyDefaultNominal();
    return () => {
      cancelled = true;
    };
  }, [expenseNominals, postingInfo?.matterID]);

  const filteredNominals = useMemo(() => {
    if (!nominalFilter) return expenseNominals;
    const search = nominalFilter.toLowerCase();
    return expenseNominals.filter(
      (item) =>
        item.nominalRef?.toLowerCase().includes(search) ||
        item.nominalName?.toLowerCase().includes(search) ||
        item.display?.toLowerCase().includes(search),
    );
  }, [expenseNominals, nominalFilter]);

  const renderRow = (text, value, minWidth = "100px", valueClass = "mb-2") => (
    <DataList
      text={text}
      value={value}
      keyClass="mb-2"
      valueClass={valueClass}
      width="w-100"
      keyStyles={{ minWidth }}
    />
  );

  const billDetailsText = `Bill Details: Date: ${formatDate(
    new Date(postingInfo?.date),
    "d",
    "en-GB",
  )}, ref: ${postingInfo?.reference ?? ""}, Amount: ${grossAmount.toFixed(2)}`;

  const canSubmit =
    !isSaving && Boolean(nominal?.nominalRef) && outstanding.amountDue > 0;

  const handleWriteOff = async () => {
    if (!canSubmit) return;

    setIsSaving(true);
    try {
      const data = [
        {
          postingType: WRITE_OFF_POSTING_TYPE,
          reference: WRITE_OFF_REFERENCE,
          details: billDetailsText,
          matterID: postingInfo?.matterID,
          netAmount: outstanding.netWriteOff,
          vatAmount: outstanding.vatToClaim,
          date: formatDateForAPI(writeOffDate),
          currencyCode: postingInfo?.currencyCode || "GBP",
          transactionPeriod: period?.period ?? getSelectedSessionPeriodNumber(),
          nominalRef: nominal.nominalRef,
          allocations: [
            {
              postingId: postingId ?? transNo,
              amount: outstanding.amountDue,
              matterID: postingInfo?.matterID,
            },
          ],
        },
      ];

      const response = await createposting(data);
      if (response?.status === 200) {
        toast.success("Bill written off successfully");
        onWriteOff?.();
      }
    } catch (error) {
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog title="Write Off Bill" onClose={onClose} className="dialog-xs">
      <Tile className="px-5 py-4 mt-3 mx-5">
        <div className="d-flex">
          <div style={{ width: "55%" }}>
            {renderRow("Matter:", matterRef || "")}
            {renderRow(
              "Date:",
              formatDate(new Date(postingInfo?.date), "d", "en-GB"),
            )}
            {renderRow("Reference:", postingInfo?.reference)}
            {renderRow("Fee Earner:", postingInfo?.feeEarnerRef)}
          </div>
          <div style={{ width: "45%" }}>
            {renderRow(
              "Net Amount:",
              AmountFormat(netAmount),
              "115px",
              "text-end mb-2",
            )}
            {renderRow(
              "VAT Amount:",
              AmountFormat(vatAmount),
              "115px",
              "text-end mb-2",
            )}
            {renderRow(
              "VAT Code:",
              `${postingInfo?.vatCode} - ${postingInfo?.vatRate} %`,
              "115px",
              "text-end mb-2",
            )}
            {renderRow(
              "Gross:",
              AmountFormat(grossAmount),
              "115px",
              "text-end mb-2",
            )}
          </div>
        </div>
        <div className="d-flex mt-1">
          <div className="w-100">{renderRow("Details:", postingInfo?.details)}</div>
        </div>
      </Tile>

      <div className="d-flex gap-3 my-3 mx-5">
        <Tile
          className="px-4"
          style={{ width: "45%", flexShrink: 0 }}
          heading="Amount Outstanding"
          headingClass="pt-1">
          {renderRow(
            "Amount Paid:",
            AmountFormat(outstanding.amountPaid),
            "110px",
            "text-end mb-2",
          )}
          {renderRow(
            "Net Write Off:",
            AmountFormat(outstanding.netWriteOff),
            "110px",
            "text-end mb-2",
          )}
          {renderRow(
            "VAT To Claim:",
            AmountFormat(outstanding.vatToClaim),
            "110px",
            "text-end mb-2",
          )}
        </Tile>

        <Tile className="d-flex flex-column flex-grow-1">
          <TransactionPeriod
            onRefreshTransactionPeriod={setPeriod}
            customStyles="p-2"
          />
          <div className="p-4">
            <div className=" d-flex flex-column mt-2">
              <Label className="mb-2">Date</Label>
              <DatePicker
                className="date-picker px-1"
                value={writeOffDate}
                onChange={(e) => setWriteOffDate(e.target.value)}
              />
            </div>
            <div className="d-flex flex-column mt-3">
              <Label className="mb-2">Write Off Nominal</Label>
              <ComboBox
                data={filteredNominals}
                textField="display"
                dataItemKey="nominalRef"
                placeholder="Select Nominal"
                className="ps-2"
                iconClassName="wa"
                value={nominal}
                filterable={true}
                onFilterChange={(e) => setNominalFilter(e.filter.value)}
                onChange={(e) => {
                  setNominalFilter("");
                  setNominal(e.value);
                }}
              />
            </div>
            <div className="d-flex justify-content-end mt-3">
              <SubmitButton
                type="button"
                className="border-0 button-small"
                onClick={handleWriteOff}
                disabled={!canSubmit}
                isLoading={isSaving}>
                Write Off
              </SubmitButton>
            </div>
          </div>
        </Tile>
      </div>

      <DialogActionsBar>
        <div className="dialog-footer w-100">
          <Button
            className="common-btn bgBtnSecondary border-0 me-5"
            onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

export default BillWriteOffDialog;
